// Per-type configuration powering the generic AssetPage (view/edit) and
// AssetSuccessPage. One entry per asset type maps that type's data shape onto a
// common view-model (card, view sections, edit JSON, populate/save) and wires
// the right composable methods. Section JSON is loaded from the server at
// runtime via getAiopSection — the local *.json files are reference only.
import { useAppState } from './useAppState';
import { useMortgage, pendingMortgageRevalidate } from './useMortgage';
import { useCredit, pendingCreditRevalidate } from './useCredit';
import { useInvestments, pendingInvestmentRevalidate } from './useInvestments';
// --- Shared pure helpers ---
function properCase(str) {
    if (!str)
        return str;
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}
function formatDate(dateString) {
    if (!dateString)
        return '';
    let date;
    const parts = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (parts) {
        date = new Date(parseInt(parts[3]), parseInt(parts[2]) - 1, parseInt(parts[1]));
    }
    else {
        date = new Date(dateString);
    }
    if (isNaN(date.getTime()))
        return dateString;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
function getBrandLogoUrl(name) {
    if (!name)
        return require('@/assets/round1.png');
    const cleaned = name.toLowerCase()
        .replace(/\./g, '')
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9 ]/g, '')
        .replace(/\s+/g, '-');
    return 'https://api.sysodev.link/BrandLogos/' + cleaned;
}
function dateToString(dateString) {
    if (!dateString)
        return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime()))
        return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
}
function stringToDate(dateString) {
    if (!dateString || dateString === 'NaN/NaN/NaN')
        return null;
    const [day, month, year] = dateString.split('/');
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
const STANDARD_FEATURES = [
    { icon: 'icon-document', text: 'Keep your account details safe,<br/>all in one place' },
    { icon: 'icon-calendar', text: 'Get reminders about<br/>important dates' },
    { icon: 'icon-chat', text: 'Chat with our assistant to<br/>understand your finances' },
];
export function useAssetConfig() {
    const state = useAppState();
    const mortgage = useMortgage();
    const credit = useCredit();
    const investments = useInvestments();
    const configs = {
        // ===================== MORTGAGE =====================
        mortgage: {
            key: 'mortgage',
            hasDocuments: false,
            title: 'Your mortgage',
            deleteLabel: 'Delete mortgage',
            menuDescription: 'Would you like to edit or delete this mortgage?',
            sectionJson: 'AddMortgagePageSections.json',
            successTitle: 'Mortgage added successfully',
            successFeatures: [
                { icon: 'icon-document', text: 'Keep your mortgage details safe,<br/>all in one place' },
                { icon: 'icon-calendar', text: 'Get reminders before your<br/>fixed deal is due to end' },
                { icon: 'icon-chat', text: 'Chat with our assistant to<br/>understand your mortgage' },
            ],
            getAccount: (id, cb, refresh, swr, poll, pollIntervalMs) => mortgage.getMortgage(id, cb, refresh, swr, poll, pollIntervalMs),
            getStateAccount: () => state.policy,
            updateAccount: (obj, cb) => mortgage.updateMortgageAccount(obj, cb),
            deleteAccount: (id, cb) => mortgage.deleteMortgageAccount(id, cb),
            revalidate: () => { pendingMortgageRevalidate.value = true; },
            populateDropdowns: (findField) => {
                credit.getCreditProviders((res) => {
                    res = res.filter((item) => item.mortgage === true)
                        .filter((item, index, self) => index === self.findIndex((t) => t.name === item.name));
                    const providerField = findField('Provider');
                    if (providerField) {
                        providerField.showAvatars = true;
                        providerField.items = res.map((x) => properCase(x.name));
                    }
                });
            },
            card: (a, fmt) => ({
                providerName: properCase(a.account?.provider?.name) || 'Provider',
                line1: 'Mortgage',
                line2: a.account?.reference || '',
                label: 'Balance',
                value: fmt.format(a.account?.balance?.amount || 0),
                avatarUrl: getBrandLogoUrl(a.account?.provider?.name),
            }),
            progressDial: (a) => (a.account?.creditCreateDate && a.dealEndDate)
                ? {
                    fromDate: a.account.creditCreateDate, toDate: a.dealEndDate, title: 'Until deal ends', footerLabel: 'Deal end date', requires: 'Deal end date',
                    messages: {
                        expired: '<b>Your deal has ended</b> – you may now be on a higher standard variable rate. Compare new deals.',
                        soon: '<b>Your deal ends soon</b> – start reviewing your options so you\'re ready to remortgage or switch.',
                        ok: '<b>You\'re all set</b> – there\'s plenty of time before your current deal ends.',
                    },
                }
                : null,
            viewSections: (a, fmt) => [
                { title: 'Mortgage details', fields: [
                        { label: 'Type', value: mortgage.getInterestRateType(a.interestRateType) },
                        { label: 'Provider', value: properCase(a.account?.provider?.name) },
                        { label: 'Account number', value: a.account?.reference },
                        { label: 'Sole or joint', value: a.account?.jointStatus != null ? (a.account.jointStatus === 0 ? 'Sole' : 'Joint') : null },
                        { label: 'Date opened', value: a.account?.creditCreateDate ? formatDate(a.account.creditCreateDate) : null },
                        { label: 'Original term', value: a.account?.termMonths ? (a.account.termMonths / 12) + ' years' : null },
                    ] },
                { title: 'Balance & repayments', fields: [
                        { label: 'Current balance', value: a.account?.balance?.amount != null ? fmt.format(a.account.balance.amount) : null },
                        { label: 'Monthly repayment', value: a.monthlyRepaymentAmount != null ? fmt.format(a.monthlyRepaymentAmount) : null },
                    ] },
                { title: 'Interest rate', fields: [
                        // Visibility gated by the JSON rules (rateType): Deal fields belong to Fixed/Discount Tracker; the plain rate to Standard Variable.
                        { label: 'Deal rate', requires: 'Deal rate', value: a.dealRate != null ? a.dealRate + '%' : null },
                        { label: 'Rate when deal ends', requires: 'Interest rate when deal ends', value: a.rate != null ? a.rate + '%' : null },
                        { label: 'Interest rate', requires: 'Interest rate', value: a.rate != null ? a.rate + '%' : null },
                        { label: 'Deal end date', requires: 'Deal end date', value: a.dealEndDate ? formatDate(a.dealEndDate) : null },
                    ] },
                { title: 'Property', fields: [
                        { label: 'Address', block: true, value: a.address ? [a.address.line1, a.address.line2, a.address.line3, a.address.town, a.address.postCode].filter(Boolean).join(', ') : null },
                    ] },
            ],
            populate: (a) => ({
                'Ownership': a.account.jointStatus === 2 ? 'Joint' : 'Sole',
                'Provider': a.account.provider.name,
                'Account number': a.account.reference,
                'Balance': a.account.balances[a.account.balances.length - 1].amount,
                'Monthly Repayment': a.monthlyRepaymentAmount,
                'Date opened': dateToString(a.account.creditCreateDate),
                'Term (Years)': a.account.termMonths / 12,
                'Type': a.interestRateType === 0 ? 'Fixed' : a.interestRateType === 1 ? 'Standard Variable' : a.interestRateType === 2 ? 'Discount Tracker' : null,
                'Deal end date': a.dealEndDate ? dateToString(a.dealEndDate) : null,
                'Deal rate': a.dealRate,
                'Interest rate when deal ends': a.rate,
                'Interest rate': a.rate,
                'Address': a.address,
            }),
            build: (f) => {
                const addressObject = f('Address') || null;
                return {
                    address: {
                        line1: addressObject?.line1 || null,
                        line2: addressObject?.line2 || null,
                        line3: addressObject?.line3 || null,
                        town: addressObject?.town || null,
                        postCode: addressObject?.postCode || null,
                    },
                    account: {
                        provider: { name: f('Provider') || null },
                        creditType: { name: f('Type') || null },
                        reference: f('Account number') || null,
                        balances: [{ amount: f('Balance') || null }],
                        balance: { amount: f('Balance') || null },
                        creditCreateDate: stringToDate(f('Date opened')) || null,
                        termMonths: f('Term (Years)') * 12 || null,
                        jointStatus: f('Ownership') === 'Joint' ? 2 : f('Ownership') === 'Sole' ? 1 : null,
                    },
                    monthlyRepaymentAmount: f('Monthly Repayment') || null,
                    dealEndDate: stringToDate(f('Deal end date')) || null,
                    dealRate: f('Deal rate') || null,
                    rate: f('Interest rate when deal ends') || f('Interest rate') || null,
                    interestRateType: f('Type') === 'Fixed' ? 0 : f('Type') === 'Standard Variable' ? 1 : f('Type') === 'Discount Tracker' ? 2 : null,
                };
            },
            editGroups: [
                { title: 'Mortgage details', labels: ['Type', 'Provider', 'Account number', 'Ownership', 'Date opened', 'Term (Years)'] },
                { title: 'Balance & repayments', labels: ['Balance', 'Monthly Repayment'] },
                { title: 'Interest rate', labels: ['Deal rate', 'Interest rate when deal ends', 'Interest rate', 'Deal end date'] },
                { title: 'Property', labels: ['Address'] },
            ],
        },
        // ===================== CREDIT / LOAN =====================
        credit: {
            key: 'credit',
            hasDocuments: true,
            title: 'Your loan',
            deleteLabel: 'Delete loan',
            menuDescription: 'Would you like to edit or delete this loan?',
            sectionJson: 'AddCreditPageSections.json',
            successTitle: 'Loan added successfully',
            successFeatures: STANDARD_FEATURES,
            getAccount: (id, cb, refresh, swr, poll, pollIntervalMs) => credit.getCreditAccount(id, cb, refresh, swr, poll, pollIntervalMs),
            getStateAccount: () => state.creditAccount,
            updateAccount: (obj, cb) => credit.updateCreditAccount(obj, cb),
            deleteAccount: (id, cb) => credit.deleteCreditAccount(id, cb),
            revalidate: () => { pendingCreditRevalidate.value = true; },
            populateDropdowns: (findField) => {
                credit.getCreditProviders((res) => {
                    res = res.filter((item) => (item.loans === true || item.creditCard === true))
                        .filter((item, index, self) => index === self.findIndex((t) => t.name === item.name));
                    const providerField = findField('Provider');
                    if (providerField) {
                        providerField.showAvatars = true;
                        providerField.items = res.map((x) => properCase(x.name));
                    }
                });
                credit.getCreditTypes((res) => {
                    res = res.filter((item) => item.name.toUpperCase() !== 'MORTGAGE');
                    const typeField = findField('Type');
                    if (typeField)
                        typeField.items = res.map((x) => properCase(x.name));
                });
            },
            card: (a, fmt) => ({
                providerName: properCase(a.provider?.name) || 'Provider',
                line1: a.creditType?.name || 'Loan',
                line2: a.reference || '',
                label: 'Balance',
                value: fmt.format(a.balance?.amount || 0),
                avatarUrl: getBrandLogoUrl(a.provider?.name),
            }),
            progressDial: () => null,
            viewSections: (a, fmt) => [
                { title: 'Loan details', fields: [
                        { label: 'Type', value: a.creditType?.name },
                        { label: 'Provider', value: properCase(a.provider?.name) },
                        { label: 'Account number', value: a.reference },
                        { label: 'Ownership', value: a.jointStatus != null ? (a.jointStatus === 2 ? 'Joint' : a.jointStatus === 1 ? 'Sole' : null) : null },
                        { label: 'Start date', value: a.creditCreateDate ? formatDate(a.creditCreateDate) : null },
                        { label: 'Term', value: a.termMonths ? a.termMonths + ' months' : null },
                    ] },
                { title: 'Balance', fields: [
                        { label: 'Balance', value: a.balance?.amount != null ? fmt.format(a.balance.amount) : null },
                    ] },
            ],
            populate: (a) => ({
                'Type': a.creditType.name,
                'Provider': a.provider.name,
                'Account number': a.reference,
                'Balance': a.balance.amount,
                'Ownership': a.jointStatus === 2 ? 'Joint' : a.jointStatus === 1 ? 'Sole' : null,
                'Start date': dateToString(a.creditCreateDate),
                'Term (months)': a.termMonths,
            }),
            build: (f) => ({
                creditType: { name: f('Type') || null },
                reference: f('Account number') || null,
                balances: [{ amount: f('Balance') || null }],
                balance: { amount: f('Balance') || null },
                provider: { name: f('Provider') || null },
                source: 1,
                termMonths: f('Term (months)') || null,
                creditCreateDate: stringToDate(f('Start date')) || null,
                jointStatus: f('Ownership') === 'Joint' ? 2 : f('Ownership') === 'Sole' ? 1 : null,
            }),
            editGroups: [
                { title: 'Loan details', labels: ['Type', 'Provider', 'Account number', 'Ownership', 'Start date', 'Term (months)'] },
                { title: 'Balance', labels: ['Balance'] },
            ],
        },
        // ===================== PENSION =====================
        pension: {
            key: 'pension',
            hasDocuments: true,
            title: 'Your pension',
            deleteLabel: 'Delete pension',
            menuDescription: 'Would you like to edit or delete this pension?',
            sectionJson: 'AddPensionPageSections.json',
            successTitle: 'Pension added successfully',
            successFeatures: [
                { icon: 'icon-document', text: 'Keep your pension details safe,<br/>all in one place' },
                { icon: 'icon-calendar', text: 'Track your retirement<br/>savings over time' },
                { icon: 'icon-chat', text: 'Chat with our assistant to<br/>understand your pension' },
            ],
            getAccount: (id, cb, refresh, swr, poll, pollIntervalMs) => investments.getInvestmentAccount(id, cb, refresh, swr, poll, pollIntervalMs),
            getStateAccount: () => state.investmentAccount,
            updateAccount: (obj, cb) => investments.updateInvestment(obj, cb),
            deleteAccount: (id, cb) => investments.deleteInvestmentAccount(id, cb),
            revalidate: () => { pendingInvestmentRevalidate.value = true; },
            populateDropdowns: (findField) => {
                investments.getInvestmentProviders((res) => {
                    res = res.filter((item) => item.pension === true)
                        .filter((item, index, self) => index === self.findIndex((t) => t.name === item.name));
                    const providerField = findField('Provider');
                    if (providerField) {
                        providerField.showAvatars = true;
                        providerField.items = res.map((x) => properCase(x.name));
                    }
                });
                investments.getInvestmentTypes((res) => {
                    res = res.filter((item) => item.name === 'Pension');
                    const typeField = findField('Type');
                    if (typeField)
                        typeField.items = res.map((x) => properCase(x.subType));
                });
            },
            card: (a, fmt) => ({
                providerName: properCase(a.provider?.name) || 'Unknown',
                line1: a.accountType?.subType || 'Pension',
                line2: a.accountNumber || '',
                label: 'Value',
                value: fmt.format(a.balance || 0),
                avatarUrl: getBrandLogoUrl(a.provider?.name),
            }),
            progressDial: () => null,
            viewSections: (a, fmt) => [
                { title: 'Pension details', fields: [
                        { label: 'Type', value: a.accountType?.subType },
                        { label: 'Provider', requires: 'Provider', value: a.provider ? properCase(a.provider.name) : 'Unknown' },
                        { label: 'Account number', value: a.accountNumber },
                        { label: 'Employer', requires: 'Employer', value: a.employer },
                        { label: 'Current employer scheme', requires: 'Current employer scheme', value: a.currentEmployerScheme == null ? null : (a.currentEmployerScheme ? 'Yes' : 'No') },
                        { label: 'Retirement age', value: a.retirementAge },
                        { label: 'Account details added', value: a.createDateTime ? formatDate(a.createDateTime) : null },
                    ] },
                { title: 'Value', fields: [
                        { label: 'Value', value: a.balance != null ? fmt.format(a.balance) : null },
                        { label: 'Personal monthly contribution', value: a.personalMonthlyContribution != null ? fmt.format(a.personalMonthlyContribution) : null },
                    ] },
            ],
            populate: (a) => ({
                'Type': a.accountType.subType,
                'Provider': a.provider ? a.provider.name : null,
                'Account number': a.accountNumber,
                'Estimated Value': a.balance,
                'Personal monthly contribution': a.personalMonthlyContribution,
                'Employer': a.employer,
                'Retirement age': a.retirementAge,
                'Current employer scheme': a.currentEmployerScheme,
            }),
            build: (f) => {
                const providerName = f('Provider') || null;
                return {
                    accountType: { name: 'Pension', subType: f('Type') || null },
                    accountNumber: f('Account number') || null,
                    provider: providerName ? { name: providerName } : null,
                    balance: f('Estimated Value') || null,
                    personalMonthlyContribution: f('Personal monthly contribution') || null,
                    employer: f('Employer') || null,
                    retirementAge: f('Retirement age') || null,
                    currentEmployerScheme: !!f('Current employer scheme'),
                };
            },
            editGroups: [
                { title: 'Pension details', labels: ['Type', 'Provider', 'Account number', 'Employer', 'Current employer scheme', 'Retirement age'] },
                { title: 'Value', labels: ['Estimated Value', 'Personal monthly contribution'] },
            ],
        },
        // ===================== INVESTMENT =====================
        investment: {
            key: 'investment',
            hasDocuments: true,
            title: 'Your investment',
            deleteLabel: 'Delete investment',
            menuDescription: 'Would you like to edit or delete this investment?',
            sectionJson: 'AddInvestmentPageSections.json',
            successTitle: 'Investment added successfully',
            successFeatures: [
                { icon: 'icon-document', text: 'Keep your investment details safe, all in one place' },
                { icon: 'icon-calendar', text: 'Track your investments over time' },
                { icon: 'icon-chat', text: 'Chat with our assistant tounderstand your investments' },
            ],
            getAccount: (id, cb, refresh, swr, poll, pollIntervalMs) => investments.getInvestmentAccount(id, cb, refresh, swr, poll, pollIntervalMs),
            getStateAccount: () => state.investmentAccount,
            updateAccount: (obj, cb) => investments.updateInvestment(obj, cb),
            deleteAccount: (id, cb) => investments.deleteInvestmentAccount(id, cb),
            revalidate: () => { pendingInvestmentRevalidate.value = true; },
            populateDropdowns: (findField) => {
                investments.getInvestmentProviders((res) => {
                    res = res.filter((item) => item.cashISA === true || item.other === true || item.investmentISA === true || item.trading === true)
                        .filter((item, index, self) => index === self.findIndex((t) => t.name === item.name));
                    const providerField = findField('Provider');
                    if (providerField) {
                        providerField.showAvatars = true;
                        providerField.items = res.map((x) => properCase(x.name));
                    }
                });
                investments.getInvestmentTypes((res) => {
                    res = res.filter((item) => item.name === 'Investment');
                    const typeField = findField('Type');
                    if (typeField)
                        typeField.items = res.map((x) => properCase(x.subType));
                });
            },
            card: (a, fmt) => ({
                providerName: properCase(a.provider?.name) || 'Unknown',
                line1: a.accountType?.subType || 'Investment',
                line2: a.accountNumber || '',
                label: 'Value',
                value: fmt.format(a.balance || 0),
                avatarUrl: getBrandLogoUrl(a.provider?.name),
            }),
            progressDial: () => null,
            viewSections: (a, fmt) => [
                { title: 'Investment details', fields: [
                        { label: 'Type', value: a.accountType?.subType },
                        { label: 'Provider', value: a.provider ? properCase(a.provider.name) : 'Unknown' },
                        { label: 'Account number', value: a.accountNumber },
                        { label: 'Account details added', value: a.createDateTime ? formatDate(a.createDateTime) : null },
                    ] },
                { title: 'Value', fields: [
                        { label: 'Value', value: a.balance != null ? fmt.format(a.balance) : null },
                        { label: 'Tax year contribution', requires: 'Allowance used this year', value: a.taxYearAllowanceUsed != null ? fmt.format(a.taxYearAllowanceUsed) : null },
                        { label: 'Ownership', requires: 'Ownership', value: a.jointStatus != null ? (a.jointStatus === 0 ? 'Sole' : 'Joint') : null },
                    ] },
            ],
            populate: (a) => ({
                'Type': a.accountType.subType,
                'Provider': a.provider ? a.provider.name : null,
                'Account number': a.accountNumber,
                'Allowance used this year': a.taxYearAllowanceUsed,
                'Estimated Value': a.balance,
                'Ownership': a.jointStatus === 2 ? 'Joint' : 'Sole',
            }),
            build: (f) => {
                const providerName = f('Provider') || null;
                return {
                    accountType: { name: 'Investment', subType: f('Type') || null },
                    accountNumber: f('Account number') || null,
                    taxYearAllowanceUsed: f('Allowance used this year') || 0,
                    balance: f('Estimated Value') || null,
                    provider: providerName ? { name: providerName } : null,
                    jointStatus: f('Ownership') === 'Joint' ? 2 : f('Ownership') === 'Sole' ? 1 : null,
                };
            },
            editGroups: [
                { title: 'Investment details', labels: ['Type', 'Provider', 'Account number'] },
                { title: 'Value', labels: ['Estimated Value', 'Allowance used this year', 'Ownership'] },
            ],
        },
    };
    function getConfig(type) {
        return configs[type];
    }
    return { getConfig };
}
